import {
    Avatar,
    Button,
    Flexer,
    Form,
    FormControl,
    FormLabel,
    Heading,
    Icon,
    IconLib,
    Input,
    InputControl,
    InputPrefix,
    Link,
    Modal,
    ModalClose,
    Notification,
    NotificationContent,
    NotificationIcon,
    Pill,
    Portal,
    Select,
    Stack,
    TBody,
    THead,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Table,
    Tabs,
    Td,
    Text,
    Th,
    Tr,
    View,
    dispatchCustomEvent,
    stopEvent,
    timezones,
    useDialog,
    useInput,
    useToast,
} from '@fold-dev/core'
import * as Token from '@fold-dev/design/tokens-es6'
import { useEffect, useRef, useState } from 'react'
import { TbAt, TbCircleMinus, TbExclamationCircle, TbMail, TbMinus, TbSquareRoundedMinus } from 'react-icons/tb'
import { Error, Loading } from '../components'
import { byEmail, getUser, logger, validEmail } from '../helpers'
import { useUserEmails } from '../hooks/api.hooks'
import { useAuth } from '../hooks/auth.hook'
import { useFile } from '../hooks/file.hook'
import { useAppStore } from '../store/app'
import { PresenceAvatar } from '../components/common/presence-avatar'

const General = (props) => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<any>(null)
    const [image, setImage] = useState('')
    const name = useInput('')
    const username = useInput('')
    const actions = useAppStore((state) => state.actions)
    const sections = useAppStore((state) => state.sections)
    const title = useInput('')
    const fileRef = useRef<any>(null)
    const { upload, getStorableUrl } = useFile()
    const { getUserId, getMe, updateMe } = useAuth()
    const [timezone, setTimezone] = useState<any>([])
    const { showToast } = useToast()

    const handleSelect = (option, dismiss) => {
        setTimezone([option.key])
        dismiss()
    }

    const handleFileChange = async (e) => {
        if (e.target.files.length == 0) return

        setLoading(true)
        setError(null)

        try {
            const { url } = await upload({
                folder: getUserId(),
                file: e.target.files[0],
                secure: false,
            })

            setImage(url)
            setLoading(false)
        } catch (e) {
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleUsernameChange = (e) => {
        const t = e.target.value.split(' ').join('')
        username.setValue(t)
    }

    const handleFileClick = (e) => (fileRef.current ? fileRef.current.click() : null)

    const handleSubmit = async (e) => {
        e.preventDefault()
        e.stopPropagation()

        setError(false)

        const id = getUserId()
        const imageSource = getStorableUrl(image)

        try {
            actions.userUpdate({
                name: name.value,
                username: username.value,
                title: title.value,
                timezone: timezone[0],
                image: imageSource,
            })

            showToast({
                message: 'Account updated.',
                anchor: 'bottom-right',
                prefix: <IconLib icon="check" />,
            })

            actions.taskUpdateUpdateUser({ user: { id, image: imageSource } }, false)

            await updateMe({
                name: name.value,
                username: username.value,
                title: title.value,
                timezone: timezone[0],
                image: imageSource,
            })

            e.target.value = ''
        } catch (e) {
            logger(e)
            setError('Error, username may be taken.')
            e.target.value = ''
        }
    }

    const fetchData = async () => {
        setLoading(false)
        setError(false)

        try {
            const user = await getMe()

            setImage(user.image)
            name.setValue(user.name)
            username.setValue(user.username)
            title.setValue(user.title)
            setTimezone([user.timezone])
            setLoading(false)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    useEffect(() => {
        fetchData()
    }, [])

    return (
        <>
            <Stack spacing={30}>
                <View>
                    <input
                        accept="image/png,image/jpg"
                        type="file"
                        className="hide"
                        ref={fileRef}
                        onChange={handleFileChange}
                    />

                    <PresenceAvatar
                        key={image ?? 'account-modal-avatar'}
                        src={image}
                        name={name.value}
                        size="xl"
                        width={150}
                        height={150}
                        className="f-buttonize"
                        onClick={handleFileClick}
                    />

                    <Button
                        m="0.5rem 0 0 0"
                        prefix={<IconLib icon="paperclip" />}
                        onClick={handleFileClick}>
                        Change image
                    </Button>
                </View>
                <View flex={1}>
                    <Error error={error} />
                    <Loading loading={loading} />

                    <Form onSubmit={handleSubmit}>
                        <Stack
                            direction="vertical"
                            spacing={20}>
                            <FormControl>
                                <FormLabel
                                    required
                                    htmlFor="name">
                                    Full name
                                </FormLabel>
                                <Input
                                    selectOnFocus
                                    id="name"
                                    value={name.value}
                                    placeholder="Full name & surname"
                                    onChange={name.onChange}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel
                                    required
                                    htmlFor="username">
                                    Username
                                </FormLabel>
                                <InputControl>
                                    <InputPrefix>
                                        <Icon
                                            icon={TbAt}
                                            style={{ color: 'var(--f-color-text-weak)' }}
                                        />
                                    </InputPrefix>
                                    <Input
                                        selectOnFocus
                                        id="username"
                                        value={username.value}
                                        placeholder="Username"
                                        onChange={handleUsernameChange}
                                    />
                                </InputControl>
                            </FormControl>

                            <FormControl>
                                <FormLabel htmlFor="title">Title</FormLabel>
                                <Input
                                    selectOnFocus
                                    id="title"
                                    value={title.value}
                                    placeholder="CTO / Founder"
                                    onChange={title.onChange}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel htmlFor="timezone">Timezone</FormLabel>
                                <Select
                                    id="timezone"
                                    width="100%"
                                    placeholder="Select a timezone"
                                    selected={timezone}
                                    onSelect={handleSelect}
                                    options={timezones.map((tz) => ({
                                        key: tz,
                                        label: tz,
                                    }))}
                                />
                            </FormControl>

                            <Button
                                type="submit"
                                variant="accent"
                                disabled={loading || !name.value}>
                                Save
                            </Button>
                        </Stack>
                    </Form>
                </View>
            </Stack>
        </>
    )
}

const Emails = (props) => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<any>(null)
    const [emails, setEmails] = useState([])
    const email = useInput('')
    const { showToast } = useToast()
    const { getUserId, getMe } = useAuth()
    const { setDialog, closeDialog } = useDialog()
    const [sent, setSent] = useState(null)
    const timeout = useRef(null)
    const userEmailsAPI = useUserEmails()

    const fetchData = async () => {
        setLoading(false)
        setError(false)

        try {
            const user = await getMe()
            setEmails(user.userEmails.sort(byEmail))
            setLoading(false)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleEmailAdd = async (e) => {
        stopEvent(e)

        setLoading(false)
        setError(false)

        if (!validEmail(email.value)) return setError('Please use a valid email address.')

        try {
            await userEmailsAPI.create({ email: email.value })
            setLoading(false)
            fetchData()
            email.setValue('')
            showToast({ message: 'Email successfully added.', anchor: 'bottom-right' })
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleEmailResend = async (userEmailId) => {
        setLoading(false)
        setError(false)

        try {
            setSent(userEmailId)
            await userEmailsAPI.resend({ userEmailId })
            setLoading(false)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleEmailDelete = async (userEmailId) => {
        setLoading(false)
        setError(false)

        try {
            await userEmailsAPI.remove({ userEmailId })
            setLoading(false)
            fetchData()
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleEmailRemove = async (userEmailId) => {
        setDialog({
            title: 'Remove this email?',
            description: 'You will no longer be able to use it to log in.',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        variant="danger"
                        onClick={() => {
                            handleEmailDelete(userEmailId)
                            closeDialog()
                        }}>
                        Delete
                    </Button>
                </View>
            ),
        })
    }

    useEffect(() => {
        if (sent) {
            clearTimeout(timeout.current)
            timeout.current = setTimeout(() => setSent(null), 2000)
        }
    }, [sent])

    useEffect(() => {
        fetchData()
    }, [])

    return (
        <>
            <Error error={error} />
            <Loading loading={loading} />

            <Form onSubmit={handleEmailAdd}>
                <View
                    row
                    gap="0.5rem"
                    m="0 0 1rem 0">
                    <InputControl>
                        <InputPrefix>
                            <Icon icon={TbMail} />
                        </InputPrefix>
                        <Input
                            type="email"
                            placeholder="Email address"
                            value={email.value}
                            onChange={email.onChange}
                        />
                    </InputControl>

                    <Button
                        type="submit"
                        variant="accent"
                        disabled={!email.value}>
                        Connect
                    </Button>
                </View>
            </Form>

            <Table
                lined
                striped={false}
                width="100%">
                <THead>
                    <Tr>
                        <Th>Email address</Th>
                        <Th>Status</Th>
                        <Th align="right"></Th>
                    </Tr>
                </THead>
                <TBody>
                    {emails.map((email: any, index: number) => (
                        <Tr key={index}>
                            <Td>
                                <Text>{email.email}</Text>
                            </Td>
                            <Td>
                                <Pill
                                    subtle
                                    size="sm"
                                    color={!email.confirmed ? Token.ColorOrange400 : Token.ColorTeal400}>
                                    {email.confirmed ? 'Connected' : 'Pending'}
                                </Pill>
                            </Td>
                            <Td align="right">
                                <View
                                    row
                                    gap="1rem"
                                    justifyContent="flex-end">
                                    {!email.confirmed && (
                                        <div style={{ width: 120 }}>
                                            <Link
                                                size="sm"
                                                onClick={() => handleEmailResend(email.id)}
                                                className="f-underline">
                                                {sent == email.id ? 'Sent!' : 'Resend confirmation'}
                                            </Link>
                                        </div>
                                    )}
                                    <Button
                                        subtle
                                        size="sm"
                                        onClick={() => handleEmailRemove(email.id)}>
                                        <Icon icon={TbCircleMinus} />
                                    </Button>
                                </View>
                            </Td>
                        </Tr>
                    ))}
                </TBody>
            </Table>
        </>
    )
}

const Security = (props) => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<any>(null)
    const passwordCurrent = useInput('')
    const passwordConfirm = useInput('')
    const passwordNew = useInput('')
    const { updateAccountPassword } = useAuth()
    const { showToast } = useToast()

    const handleSubmit = async (e) => {
        e.preventDefault()
        e.stopPropagation()

        setLoading(false)
        setError(false)

        if (passwordConfirm.value != passwordNew.value) return setError('Passwords do not match')
        if (passwordNew.value == passwordCurrent.value) return setError('New password is the same as old password.')

        try {
            await updateAccountPassword({
                newPassword: passwordNew.value,
                oldPassword: passwordCurrent.value,
            })

            passwordConfirm.setValue('')
            passwordCurrent.setValue('')
            passwordNew.setValue('')

            setLoading(false)

            showToast({
                message: 'Password succussfully updated',
                anchor: 'bottom-right',
            })
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    return (
        <Form onSubmit={handleSubmit}>
            <Error error={error} />
            <Loading loading={loading} />

            <Stack
                direction="vertical"
                spacing={20}>
                <FormControl>
                    <FormLabel
                        required
                        htmlFor="password-current">
                        Current Password
                    </FormLabel>
                    <Input
                        id="password-current"
                        type="password"
                        value={passwordCurrent.value}
                        placeholder="Current password"
                        onChange={passwordCurrent.onChange}
                    />
                </FormControl>

                <FormControl>
                    <FormLabel
                        required
                        htmlFor="password-confirm">
                        Confirm Current Password
                    </FormLabel>
                    <Input
                        id="password-confirm"
                        type="password"
                        value={passwordConfirm.value}
                        placeholder="Confirm current password"
                        onChange={passwordConfirm.onChange}
                    />
                </FormControl>

                <FormControl>
                    <FormLabel
                        required
                        htmlFor="password-new">
                        New Password
                    </FormLabel>
                    <Input
                        id="password-new"
                        type="password"
                        value={passwordNew.value}
                        placeholder="New password"
                        onChange={passwordNew.onChange}
                    />
                </FormControl>

                <Button
                    type="submit"
                    variant="accent"
                    disabled={loading || !passwordCurrent.value || !passwordConfirm.value || !passwordNew.value}>
                    Update
                </Button>
            </Stack>
        </Form>
    )
}

export const AccountModal = (props: any) => {
    const { onDismiss } = props
    const [selected, setSelected] = useState(0)

    return (
        <Modal
            disableBackgroundDismiss
            width={800}
            height="90%"
            portal={Portal}
            anchor="middle-center"
            onDismiss={onDismiss}
            header={
                <>
                    <Heading as="h4">Account Settings</Heading>
                    <Flexer />
                    <ModalClose onClick={onDismiss} />
                </>
            }
            isVisible={true}
            style={{ '--f-modal-body-padding': 0 }}>
            <Tabs
                layout="left"
                selected={selected}
                onSelect={(index) => setSelected(index)}>
                <TabList>
                    <Tab>Profile</Tab>
                    <Tab>Security</Tab>
                    <Tab>Email Addresses</Tab>
                    <Tab>Danger zone</Tab>
                </TabList>

                <TabPanels>
                    <TabPanel>
                        <General />
                    </TabPanel>
                    <TabPanel>
                        <Security />
                    </TabPanel>
                    <TabPanel>
                        <Emails />
                    </TabPanel>
                    <TabPanel>
                        <Stack
                            direction="vertical"
                            spacing={15}>
                            <Notification
                                radius="var(--f-radius)"
                                variant="highlight"
                                width="100%">
                                <NotificationIcon>
                                    <Icon icon={TbExclamationCircle} />
                                </NotificationIcon>
                                <NotificationContent>
                                    Unfortunately we don't support deleting accounts via the app (yet). We're working to
                                    add this. If you want to remove your account, please email us at{' '}
                                    <a href="mailto:accounts@fronteer.app">accounts@fronteer.app</a>.
                                </NotificationContent>
                            </Notification>
                        </Stack>
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Modal>
    )
}
